import { getAnalytics, logEvent, setAnalyticsCollectionEnabled, setUserProperties } from 'firebase/analytics';
import { app, firebaseClient } from '@cvt/clients/firebaseClient';


const analytics = getAnalytics(app);

// An admin viewing the hub as a homeowner must not show up as that homeowner
// in GA. The flag is fixed for the tab's lifetime and collection is per tab,
// so switching it off here, at load, silences every event in that tab only.
const collectionBlocked = firebaseClient.impersonation.isActive();
if (collectionBlocked) {
  setAnalyticsCollectionEnabled(analytics, false);
}

export const trackPageview = () => {
  logEvent(analytics, 'page_view', {
    page_path: window.location.pathname,
    page_title: document.title,
    page_location: window.location.href,
  });
};

export const trackSignUp = () => {
  logEvent(analytics, 'sign_up', {
    content_type: 'user',
  });
};

export const trackLogIn = () => {
  logEvent(analytics, 'login', {
    content_type: 'user',
  });
};

export const setUserEmailDimension = (email: string) => {
  setUserProperties(analytics, { email });
};

export const setUserCompanyDimension = (company: string) => {
  setUserProperties(analytics, { company });
};

/**
 * Firebase re-enables collection on every page load, so a stored "declined"
 * only holds if the hub re-applies it on mount — see HubCookieBanner. The CRM
 * has no banner and never calls this, so it keeps the enabled default.
 */
export const applyAnalyticsConsent = (granted: boolean) => {
  // Stored consent is re-applied on every hub mount; it must not re-enable
  // collection in an impersonating tab.
  setAnalyticsCollectionEnabled(analytics, granted && !collectionBlocked);
};

// The hub and the CRM share one GA4 property, so every hub event carries
// app_area and homeowner behaviour stays separable from staff behaviour.
const hubEvent = (name: string, params: Record<string, unknown> = {}) => {
  logEvent(analytics, name, { app_area: 'hub', ...params });
};

export const trackHubLoginLinkRequested = () => {
  hubEvent('hub_login_link_requested');
};

export const trackHubLogin = () => {
  hubEvent('hub_login', { method: 'email_link' });
};

export const trackHubExploreViewed = () => {
  hubEvent('hub_explore_viewed');
};

export const trackHubModuleCompleted = (moduleId: string, score: number) => {
  hubEvent('hub_module_completed', { module_id: moduleId, score });
};

export const trackHubUtilityConnected = (utility?: string | null) => {
  hubEvent('hub_utility_connected', { utility: utility || 'unknown' });
};

export const trackHubUtilityBillUploaded = () => {
  hubEvent('hub_utility_bill_uploaded');
};

export const trackHubGoalsSet = (prioritiesCount: number) => {
  hubEvent('hub_goals_set', { priorities_count: prioritiesCount });
};

export const trackHubPhotoUploaded = (category: string) => {
  hubEvent('hub_photo_uploaded', { category });
};
